import { ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

// * Combining clsx or classnames with tailwind-merge allows us to conditionally join
// * Tailwind CSS classes in classNames together without style conflicts.
// * Inspired by shadcn/ui.
export function cn(...args: ClassValue[]) {
  return twMerge(clsx(args));
}

// * Returns local storage item for the given key.
// * If it does not exist it returns the default value, which can be undefined.
export const getLocalStorageItem = (
  key: string,
  defaultValue?: string
): string | undefined => {
  if (!window || !window.localStorage) {
    return undefined;
  }

  const item = window.localStorage.getItem(key);

  if (item) {
    return item;
  } else {
    return defaultValue;
  }
};

// * Deletes local storage item with the given key
export const deleteLocalStorageItem = (key: string): void => {
  if (!window || !window.localStorage) {
    return;
  }

  window.localStorage.removeItem(key);
};

// * Method to prepare the HTTP request headers.
// * It is mostly used on RTK API slices definition.
// * Currently, it sets the `Authorization` header if the access_token is set on local storage.
export function prepareHeaders(headers: Headers) {
  const token = getLocalStorageItem('access_token');

  // * If a JWT is stored on the local or session storage then set it to the request header
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
    headers.set('Proxy-Authorization', `Bearer ${token}`);
  }

  return headers;
}

export function validateNIF(nif: string) {
  if (nif.length !== 9) return false;

  const checkDigit = parseInt(nif[8], 10);
  const baseNumber = nif.slice(0, 8);

  const total = baseNumber.split('').reduce((acc, num, index) => {
    return acc + parseInt(num, 10) * (9 - index);
  }, 0);

  const remainder = total % 11;
  const calculatedCheckDigit = remainder < 2 ? 0 : 11 - remainder;

  return checkDigit === calculatedCheckDigit;
}

export function validateNISS(niss: string): boolean {
  const table = [29, 23, 19, 17, 13, 11, 7, 5, 3, 2];
  const LEN_NISS = 11;

  // Verificar tamanho do NISS
  if (niss.length !== LEN_NISS) {
    return false;
  }

  // Verificar o primeiro dígito
  if (!['1', '2'].includes(niss[0])) {
    return false;
  }

  // Converter o NISS para lista de inteiros
  const nissArray = niss.split('').map(Number);

  // Calcular soma de controlo
  const sum = nissArray
    .slice(0, -1)
    .reduce((acc, num, idx) => acc + num * table[idx], 0);

  // Verificar se o último dígito bate com a soma de controlo
  return nissArray[nissArray.length - 1] === 9 - (sum % 10);
}
